import React, { useState } from 'react';
import { Database, Layers, Shield, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DataModel: React.FC = () => {
  const { t } = useTranslation();
  const [activeCollection, setActiveCollection] = useState('vendors');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedExamples, setExpandedExamples] = useState<{ [key: string]: boolean }>({});

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleExample = (id: string) => {
    setExpandedExamples(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const collections = [
    { id: 'vendors', name: 'vendors', icon: '🏢', color: 'text-blue-600' },
    { id: 'promoters', name: 'promoters', icon: '👥', color: 'text-green-600' },
    { id: 'prospects', name: 'prospects', icon: '🎯', color: 'text-purple-600' },
    { id: 'meetings', name: 'meetings', icon: '📅', color: 'text-orange-600' },
    { id: 'testimonials', name: 'testimonials', icon: '🎥', color: 'text-red-600' },
    { id: 'useCases', name: 'useCases', icon: '📝', color: 'text-indigo-600' },
    { id: 'reviews', name: 'reviews', icon: '⭐', color: 'text-yellow-600' },
    { id: 'rewards', name: 'rewards', icon: '🎁', color: 'text-pink-600' },
    { id: 'teamMembers', name: 'teamMembers', icon: '👨‍💼', color: 'text-teal-600' },
    { id: 'oneToOneCalls', name: 'oneToOneCalls', icon: '📞', color: 'text-cyan-600' }
  ];

  const collectionData: { [key: string]: any } = {
    vendors: {
      title: 'vendors',
      description: 'Informations sur les entreprises clientes (multi-tenant)',
      examples: [
        {
          name: 'Entreprise Enterprise Active',
          document: `{
  "_id": "vendor_uuid_123",
  "companyName": "TechCorp Solutions",
  "email": "contact@techcorp.com",
  "phone": "+33 1 23 45 67 89",
  "industry": "Technology",
  "website": "https://techcorp.com",
  "logo": "https://cdn.clareefai.com/logos/techcorp.png",
  "subscription": {
    "plan": "enterprise",
    "status": "active",
    "startDate": "2024-01-01T00:00:00Z",
    "renewalDate": "2025-01-01T00:00:00Z",
    "features": ["unlimited_promoters", "advanced_analytics", "api_access"]
  },
  "settings": {
    "timezone": "Europe/Paris",
    "currency": "EUR",
    "language": "en",
    "brandColors": {
      "primary": "#3B82F6",
      "secondary": "#10B981"
    }
  },
  "createdAt": "2024-01-01T00:00:00Z",
  "updatedAt": "2024-02-15T10:30:00Z"
}`
        },
        {
          name: 'PME Plan Starter',
          document: `{
  "_id": "vendor_uuid_456",
  "companyName": "Marketing Plus",
  "email": "hello@marketingplus.com",
  "phone": "+33 6 78 90 12 34",
  "industry": "Marketing",
  "website": "https://marketingplus.fr",
  "logo": "https://cdn.clareefai.com/logos/marketingplus.png",
  "subscription": {
    "plan": "starter",
    "status": "active",
    "startDate": "2024-02-01T00:00:00Z",
    "renewalDate": "2024-03-01T00:00:00Z",
    "features": ["10_promoters", "basic_analytics"]
  },
  "settings": {
    "timezone": "Europe/Paris",
    "currency": "EUR",
    "language": "en",
    "brandColors": {
      "primary": "#F59E0B",
      "secondary": "#EF4444"
    }
  },
  "createdAt": "2024-02-01T00:00:00Z",
  "updatedAt": "2024-02-15T14:22:00Z"
}`
        },
        {
          name: 'Entreprise en Trial',
          document: `{
  "_id": "vendor_uuid_789",
  "companyName": "HealthTech Innovations",
  "email": "info@healthtech.io",
  "phone": "+1 555 123 4567",
  "industry": "Healthcare",
  "website": "https://healthtech.io",
  "logo": "https://cdn.clareefai.com/logos/healthtech.png",
  "subscription": {
    "plan": "trial",
    "status": "trial",
    "startDate": "2024-02-10T00:00:00Z",
    "renewalDate": "2024-02-24T00:00:00Z",
    "features": ["5_promoters", "basic_analytics"],
    "trialEndsAt": "2024-02-24T23:59:59Z"
  },
  "settings": {
    "timezone": "America/New_York",
    "currency": "USD",
    "language": "en",
    "brandColors": {
      "primary": "#06B6D4",
      "secondary": "#8B5CF6"
    }
  },
  "createdAt": "2024-02-10T00:00:00Z",
  "updatedAt": "2024-02-10T00:00:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique du vendor' },
        { name: 'companyName', type: 'string', required: true, description: 'Nom de l\'entreprise' },
        { name: 'email', type: 'string', required: true, description: 'Email de contact' },
        { name: 'phone', type: 'string', required: false, description: 'Numéro de téléphone' },
        { name: 'industry', type: 'string', required: false, description: 'Secteur d\'activité' },
        { name: 'website', type: 'string', required: false, description: 'Site web' },
        { name: 'logo', type: 'string', required: false, description: 'URL du logo' },
        { name: 'subscription', type: 'object', required: true, description: 'Informations d\'abonnement' },
        { name: 'settings', type: 'object', required: true, description: 'Paramètres du vendor' },
        { name: 'createdAt', type: 'timestamp', required: true, description: 'Date de création' },
        { name: 'updatedAt', type: 'timestamp', required: true, description: 'Date de mise à jour' }
      ]
    },
    promoters: {
      title: 'promoters',
      description: 'Clients promoteurs qui recommandent vos produits',
      examples: [
        {
          name: 'Promoteur Invited (nouveau)',
          document: `{
  "_id": "promoter_uuid_001",
  "vendorId": "vendor_uuid_123",
  "name": "Michel Dubois",
  "email": "michel.dubois@company.fr",
  "phone": "+33 1 23 45 67 89",
  "company": "Tech Solutions",
  "companySize": "51-200 employees",
  "avatar": "https://api.dicebear.com/7.x/avataaars/svg?seed=Michel",
  "status": "invited",
  "sectors": ["Technology", "Services"],
  "prospectsReferred": 0,
  "conversionRate": 0,
  "totalRewards": 0,
  "joinedDate": "2024-01-10T00:00:00Z",
  "vipStatus": {
    "isVIP": false,
    "requestedAt": null,
    "approvedAt": null,
    "benefits": []
  },
  "progress": {
    "reviewCompleted": false,
    "testimonialCompleted": false,
    "useCaseCompleted": false,
    "firstMeetingCompleted": false
  },
  "metadata": {
    "source": "manual",
    "tags": ["tech", "enterprise"],
    "customFields": {}
  },
  "createdAt": "2024-01-10T00:00:00Z",
  "updatedAt": "2024-01-10T00:00:00Z"
}`
        },
        {
          name: 'Promoteur VIP Actif',
          document: `{
  "_id": "promoter_uuid_002",
  "vendorId": "vendor_uuid_123",
  "name": "Sophie Laurent",
  "email": "sophie.laurent@bigcorp.com",
  "phone": "+33 6 12 34 56 78",
  "company": "BigCorp International",
  "companySize": "1000+ employees",
  "avatar": "https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie",
  "status": "testimonial_done",
  "sectors": ["Finance", "Technology"],
  "prospectsReferred": 15,
  "conversionRate": 73,
  "totalRewards": 7500,
  "joinedDate": "2023-06-15T00:00:00Z",
  "vipStatus": {
    "isVIP": true,
    "requestedAt": "2023-07-01T00:00:00Z",
    "approvedAt": "2023-07-03T00:00:00Z",
    "benefits": ["priority_support", "exclusive_events", "increased_rewards"]
  },
  "progress": {
    "reviewCompleted": true,
    "testimonialCompleted": true,
    "useCaseCompleted": true,
    "firstMeetingCompleted": true
  },
  "metadata": {
    "source": "referral",
    "tags": ["vip", "high-value", "advocate"],
    "customFields": {
      "industry_influence": "high",
      "social_media_reach": "50k+"
    }
  },
  "createdAt": "2023-06-15T00:00:00Z",
  "updatedAt": "2024-02-15T14:20:00Z"
}`
        },
        {
          name: 'Promoteur Review Pending',
          document: `{
  "_id": "promoter_uuid_003",
  "vendorId": "vendor_uuid_123",
  "name": "Thomas Martin",
  "email": "thomas.martin@startup.io",
  "phone": "+33 7 89 01 23 45",
  "company": "InnovateTech",
  "companySize": "11-50 employees",
  "avatar": "https://api.dicebear.com/7.x/avataaars/svg?seed=Thomas",
  "status": "review",
  "sectors": ["Technology", "SaaS"],
  "prospectsReferred": 2,
  "conversionRate": 50,
  "totalRewards": 500,
  "joinedDate": "2024-01-20T00:00:00Z",
  "vipStatus": {
    "isVIP": false,
    "requestedAt": null,
    "approvedAt": null,
    "benefits": []
  },
  "progress": {
    "reviewCompleted": false,
    "testimonialCompleted": false,
    "useCaseCompleted": false,
    "firstMeetingCompleted": true
  },
  "metadata": {
    "source": "widget",
    "tags": ["startup", "early-adopter"],
    "customFields": {}
  },
  "createdAt": "2024-01-20T00:00:00Z",
  "updatedAt": "2024-02-10T09:30:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'name', type: 'string', required: true, description: 'Nom complet' },
        { name: 'email', type: 'string', required: true, description: 'Email' },
        { name: 'phone', type: 'string', required: false, description: 'Téléphone' },
        { name: 'company', type: 'string', required: true, description: 'Entreprise' },
        { name: 'companySize', type: 'string', required: false, description: 'Taille de l\'entreprise' },
        { name: 'avatar', type: 'string', required: false, description: 'URL de l\'avatar' },
        { name: 'status', type: 'string', required: true, description: 'Statut: invited, review, available, testimonial_ready, testimonial_done' },
        { name: 'sectors', type: 'array', required: false, description: 'Secteurs d\'activité' },
        { name: 'prospectsReferred', type: 'number', required: true, description: 'Nombre de prospects référés' },
        { name: 'conversionRate', type: 'number', required: true, description: 'Taux de conversion (%)' },
        { name: 'totalRewards', type: 'number', required: true, description: 'Total des récompenses' },
        { name: 'vipStatus', type: 'object', required: true, description: 'Statut VIP' },
        { name: 'progress', type: 'object', required: true, description: 'Progression du promoteur' }
      ]
    },
    prospects: {
      title: 'prospects',
      description: 'Prospects référés par les promoteurs',
      examples: [
        {
          name: 'Prospect Request Sent',
          document: `{
  "_id": "prospect_uuid_001",
  "vendorId": "vendor_uuid_123",
  "name": "Sarah Johnson",
  "email": "sarah.johnson@techcorp.com",
  "phone": "+33 1 56 78 90 23",
  "company": "Digital Corp",
  "companySize": "51-200 employees",
  "sectors": ["Technology", "AI"],
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "assignedPromoters": ["promoter_uuid_002"],
  "status": "request_sent",
  "assignedTo": "Commercial A",
  "assignedSalespersonId": "team_member_uuid_001",
  "notes": "Recherche une solution pour optimiser leur workflow",
  "source": "promoter_referral",
  "createdDate": "2024-01-19T00:00:00Z",
  "lastContactDate": "2024-02-10T09:15:00Z",
  "expectedValue": 50000,
  "timeline": {
    "introduced": "2024-01-19T00:00:00Z",
    "requestSent": "2024-01-22T10:00:00Z",
    "scheduled": null,
    "completed": null
  },
  "metadata": {
    "utmSource": "email",
    "utmCampaign": "q1_2024",
    "customFields": {}
  },
  "createdAt": "2024-01-19T00:00:00Z",
  "updatedAt": "2024-02-10T09:15:00Z"
}`
        },
        {
          name: 'Prospect Meeting Scheduled',
          document: `{
  "_id": "prospect_uuid_002",
  "vendorId": "vendor_uuid_123",
  "name": "Jean Dupont",
  "email": "jean.dupont@retail-co.fr",
  "phone": "+33 6 45 67 89 01",
  "company": "Retail Solutions France",
  "companySize": "201-500 employees",
  "sectors": ["Retail", "E-commerce"],
  "promoterId": "promoter_uuid_001",
  "promoterName": "Michel Dubois",
  "assignedPromoters": ["promoter_uuid_001"],
  "status": "scheduled",
  "assignedTo": "Commercial B",
  "assignedSalespersonId": "team_member_uuid_002",
  "notes": "Très intéressé par notre solution Analytics. Meeting planifié pour le 20 février.",
  "source": "widget_submission",
  "createdDate": "2024-02-05T00:00:00Z",
  "lastContactDate": "2024-02-12T14:30:00Z",
  "expectedValue": 75000,
  "timeline": {
    "introduced": "2024-02-05T00:00:00Z",
    "requestSent": "2024-02-08T09:00:00Z",
    "scheduled": "2024-02-12T14:30:00Z",
    "completed": null
  },
  "metadata": {
    "utmSource": "widget",
    "utmCampaign": "winter_2024",
    "customFields": {
      "urgency": "high",
      "budget_confirmed": true
    }
  },
  "createdAt": "2024-02-05T00:00:00Z",
  "updatedAt": "2024-02-12T14:30:00Z"
}`
        },
        {
          name: 'Prospect Completed (Converti)',
          document: `{
  "_id": "prospect_uuid_003",
  "vendorId": "vendor_uuid_123",
  "name": "Marie Lambert",
  "email": "marie.lambert@consulting-firm.com",
  "phone": "+33 1 98 76 54 32",
  "company": "Lambert Consulting",
  "companySize": "11-50 employees",
  "sectors": ["Consulting", "Strategy"],
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "assignedPromoters": ["promoter_uuid_002"],
  "status": "completed",
  "assignedTo": "Commercial A",
  "assignedSalespersonId": "team_member_uuid_001",
  "notes": "Client converti! Contract signé pour 60k EUR annuel.",
  "source": "promoter_referral",
  "createdDate": "2024-01-05T00:00:00Z",
  "lastContactDate": "2024-01-30T16:45:00Z",
  "expectedValue": 60000,
  "actualValue": 60000,
  "timeline": {
    "introduced": "2024-01-05T00:00:00Z",
    "requestSent": "2024-01-08T10:00:00Z",
    "scheduled": "2024-01-12T14:00:00Z",
    "completed": "2024-01-30T16:45:00Z"
  },
  "metadata": {
    "utmSource": "email",
    "utmCampaign": "q1_2024",
    "customFields": {
      "contract_type": "annual",
      "payment_terms": "quarterly"
    }
  },
  "createdAt": "2024-01-05T00:00:00Z",
  "updatedAt": "2024-01-30T16:45:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'name', type: 'string', required: true, description: 'Nom complet' },
        { name: 'email', type: 'string', required: true, description: 'Email' },
        { name: 'phone', type: 'string', required: false, description: 'Téléphone' },
        { name: 'company', type: 'string', required: true, description: 'Entreprise' },
        { name: 'promoterId', type: 'string', required: false, description: 'ID du promoteur référent' },
        { name: 'promoterName', type: 'string', required: false, description: 'Nom du promoteur (dénormalisé)' },
        { name: 'status', type: 'string', required: true, description: 'Statut: introduce, request_sent, scheduled, completed, failed' },
        { name: 'assignedTo', type: 'string', required: false, description: 'Commercial assigné' },
        { name: 'expectedValue', type: 'number', required: false, description: 'Valeur attendue du deal' },
        { name: 'timeline', type: 'object', required: true, description: 'Chronologie des étapes' }
      ]
    },
    meetings: {
      title: 'meetings',
      description: 'Réunions entre promoteurs et prospects',
      examples: [
        {
          name: 'Meeting Request (en attente)',
          document: `{
  "_id": "meeting_uuid_001",
  "vendorId": "vendor_uuid_123",
  "prospectId": "prospect_uuid_001",
  "prospectName": "Sarah Johnson",
  "prospectEmail": "sarah.johnson@techcorp.com",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "date": "2024-02-20",
  "time": "14:00",
  "duration": 60,
  "timezone": "Europe/Paris",
  "status": "request",
  "meetingType": "introduction",
  "location": {
    "type": "video",
    "url": "https://meet.google.com/xyz-abc-def",
    "platform": "Google Meet"
  },
  "agenda": "Introduction et présentation de la solution",
  "notes": "",
  "outcome": null,
  "followUpRequired": false,
  "attendees": [
    {
      "name": "Sophie Laurent",
      "email": "sophie.laurent@bigcorp.com",
      "role": "promoter",
      "confirmed": false
    }
  ],
  "reminders": [],
  "createdAt": "2024-02-10T15:30:00Z",
  "updatedAt": "2024-02-10T15:30:00Z"
}`
        },
        {
          name: 'Meeting Booked (confirmé)',
          document: `{
  "_id": "meeting_uuid_002",
  "vendorId": "vendor_uuid_123",
  "prospectId": "prospect_uuid_002",
  "prospectName": "Jean Dupont",
  "prospectEmail": "jean.dupont@retail-co.fr",
  "promoterId": "promoter_uuid_001",
  "promoterName": "Michel Dubois",
  "date": "2024-02-20",
  "time": "10:00",
  "duration": 45,
  "timezone": "Europe/Paris",
  "status": "booked",
  "meetingType": "product_demo",
  "location": {
    "type": "video",
    "url": "https://zoom.us/j/123456789",
    "platform": "Zoom"
  },
  "agenda": "Démonstration produit et Q&A",
  "notes": "Préparer demo environnement e-commerce",
  "outcome": null,
  "followUpRequired": false,
  "attendees": [
    {
      "name": "Michel Dubois",
      "email": "michel.dubois@company.fr",
      "role": "promoter",
      "confirmed": true
    },
    {
      "name": "Commercial A",
      "email": "commercial.a@company.com",
      "role": "salesperson",
      "confirmed": true
    }
  ],
  "reminders": [
    {
      "type": "email",
      "sentAt": "2024-02-19T10:00:00Z",
      "status": "sent"
    }
  ],
  "createdAt": "2024-02-12T14:30:00Z",
  "updatedAt": "2024-02-19T10:00:00Z"
}`
        },
        {
          name: 'Meeting Completed (terminé)',
          document: `{
  "_id": "meeting_uuid_003",
  "vendorId": "vendor_uuid_123",
  "prospectId": "prospect_uuid_003",
  "prospectName": "Marie Lambert",
  "prospectEmail": "marie.lambert@consulting-firm.com",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "date": "2024-01-25",
  "time": "15:00",
  "duration": 60,
  "timezone": "Europe/Paris",
  "status": "completed",
  "meetingType": "introduction",
  "location": {
    "type": "video",
    "url": "https://meet.google.com/completed-123",
    "platform": "Google Meet"
  },
  "agenda": "Introduction et présentation initiale",
  "notes": "Excellent meeting! Prospect très intéressé.",
  "outcome": "positive",
  "followUpRequired": true,
  "attendees": [
    {
      "name": "Sophie Laurent",
      "email": "sophie.laurent@bigcorp.com",
      "role": "promoter",
      "confirmed": true
    },
    {
      "name": "Commercial A",
      "email": "commercial.a@company.com",
      "role": "salesperson",
      "confirmed": true
    }
  ],
  "reminders": [
    {
      "type": "email",
      "sentAt": "2024-01-24T10:00:00Z",
      "status": "sent"
    }
  ],
  "completedAt": "2024-01-25T16:00:00Z",
  "createdAt": "2024-01-15T09:00:00Z",
  "updatedAt": "2024-01-25T16:00:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'prospectId', type: 'string', required: true, description: 'ID du prospect' },
        { name: 'promoterId', type: 'string', required: true, description: 'ID du promoteur' },
        { name: 'date', type: 'string', required: true, description: 'Date du meeting (YYYY-MM-DD)' },
        { name: 'time', type: 'string', required: true, description: 'Heure du meeting (HH:MM)' },
        { name: 'status', type: 'string', required: true, description: 'Statut: request, progress, booked, completed' },
        { name: 'location', type: 'object', required: true, description: 'Lieu/URL du meeting' },
        { name: 'attendees', type: 'array', required: true, description: 'Liste des participants' }
      ]
    },
    testimonials: {
      title: 'testimonials',
      description: 'Témoignages vidéo des promoteurs',
      examples: [
        {
          name: 'Testimonial Request (demandé)',
          document: `{
  "_id": "testimonial_uuid_001",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_001",
  "promoterName": "Michel Dubois",
  "promoterCompany": "Tech Solutions",
  "type": "request",
  "title": "",
  "content": "",
  "videoUrl": null,
  "thumbnailUrl": null,
  "duration": null,
  "quality": null,
  "status": "sent",
  "requestDate": "2024-02-10T00:00:00Z",
  "submissionDate": null,
  "publishDate": null,
  "product": "Clareefai Analytics",
  "featured": false,
  "engagement": {
    "views": 0,
    "likes": 0,
    "shares": 0,
    "comments": 0
  },
  "createdAt": "2024-02-10T00:00:00Z",
  "updatedAt": "2024-02-10T00:00:00Z"
}`
        },
        {
          name: 'Testimonial Pending Review',
          document: `{
  "_id": "testimonial_uuid_002",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_003",
  "promoterName": "Thomas Martin",
  "promoterCompany": "InnovateTech",
  "type": "submission",
  "title": "Great product for startups",
  "content": "Clareefai has been instrumental in our growth journey...",
  "videoUrl": "https://cdn.clareefai.com/videos/testimonial_002.mp4",
  "thumbnailUrl": "https://cdn.clareefai.com/thumbnails/testimonial_002.jpg",
  "duration": "1:45",
  "quality": "medium",
  "status": "pending_review",
  "requestDate": "2024-01-25T00:00:00Z",
  "submissionDate": "2024-02-08T16:20:00Z",
  "publishDate": null,
  "product": "Clareefai Analytics",
  "featured": false,
  "engagement": {
    "views": 0,
    "likes": 0,
    "shares": 0,
    "comments": 0
  },
  "createdAt": "2024-01-25T00:00:00Z",
  "updatedAt": "2024-02-08T16:20:00Z"
}`
        },
        {
          name: 'Testimonial Published (publié)',
          document: `{
  "_id": "testimonial_uuid_003",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "promoterCompany": "BigCorp International",
  "type": "submission",
  "title": "A revolutionary solution for enterprise",
  "content": "Clareefai has transformed our business processes and customer engagement strategy...",
  "videoUrl": "https://cdn.clareefai.com/videos/testimonial_003.mp4",
  "thumbnailUrl": "https://cdn.clareefai.com/thumbnails/testimonial_003.jpg",
  "duration": "2:30",
  "quality": "high",
  "status": "published",
  "requestDate": "2024-01-05T00:00:00Z",
  "submissionDate": "2024-01-15T14:20:00Z",
  "publishDate": "2024-01-18T10:00:00Z",
  "product": "Clareefai Analytics",
  "featured": true,
  "engagement": {
    "views": 1247,
    "likes": 89,
    "shares": 23,
    "comments": 12
  },
  "createdAt": "2024-01-05T00:00:00Z",
  "updatedAt": "2024-02-10T16:45:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'promoterId', type: 'string', required: true, description: 'ID du promoteur' },
        { name: 'type', type: 'string', required: true, description: 'Type: request ou submission' },
        { name: 'title', type: 'string', required: true, description: 'Titre du témoignage' },
        { name: 'videoUrl', type: 'string', required: false, description: 'URL de la vidéo' },
        { name: 'status', type: 'string', required: true, description: 'Statut: sent, pending_review, approved, published' },
        { name: 'quality', type: 'string', required: false, description: 'Qualité: high, medium, low' },
        { name: 'engagement', type: 'object', required: false, description: 'Métriques d\'engagement' }
      ]
    },
    useCases: {
      title: 'useCases',
      description: 'Cas d\'usage clients détaillés',
      examples: [
        {
          name: 'Use Case Request (demandé)',
          document: `{
  "_id": "usecase_uuid_001",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_001",
  "promoterName": "Michel Dubois",
  "promoterCompany": "Tech Solutions",
  "type": "request",
  "title": "",
  "slug": "",
  "description": "",
  "content": "",
  "industry": "",
  "product": "Clareefai Analytics",
  "challenge": "",
  "solution": "",
  "results": null,
  "benefits": [],
  "status": "sent",
  "requestDate": "2024-02-12T00:00:00Z",
  "createdAt": "2024-02-12T00:00:00Z",
  "updatedAt": "2024-02-12T00:00:00Z"
}`
        },
        {
          name: 'Use Case Draft (brouillon)',
          document: `{
  "_id": "usecase_uuid_002",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_003",
  "promoterName": "Thomas Martin",
  "promoterCompany": "InnovateTech",
  "type": "submission",
  "title": "Scaling customer advocacy for startups",
  "slug": "scaling-customer-advocacy-startups",
  "description": "How InnovateTech used Clareefai to build a thriving customer advocacy program from scratch",
  "content": "When we started, we had only 10 customers...",
  "industry": "Technology",
  "product": "Clareefai Analytics",
  "challenge": "Building customer advocacy with limited resources",
  "solution": "Implementing Clareefai to automate and scale advocacy efforts",
  "results": {
    "metrics": [
      {
        "name": "Advocates acquired",
        "value": "+25",
        "description": "From 0 to 25 active advocates in 6 months"
      }
    ],
    "roi": "180%",
    "timeframe": "6 months"
  },
  "benefits": [
    "Built structured advocacy program",
    "Reduced customer acquisition cost by 30%"
  ],
  "status": "draft",
  "requestDate": "2024-01-30T00:00:00Z",
  "submissionDate": "2024-02-10T11:15:00Z",
  "createdAt": "2024-01-30T00:00:00Z",
  "updatedAt": "2024-02-10T11:15:00Z"
}`
        },
        {
          name: 'Use Case Published (publié)',
          document: `{
  "_id": "usecase_uuid_003",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "promoterCompany": "BigCorp International",
  "type": "submission",
  "title": "Enterprise-wide customer advocacy transformation",
  "slug": "enterprise-customer-advocacy-transformation",
  "description": "How BigCorp transformed their customer relationships with Clareefai Analytics",
  "content": "BigCorp International faced challenges engaging their 500+ customers effectively. With Clareefai Analytics, they built a comprehensive customer advocacy program...",
  "industry": "Finance",
  "product": "Clareefai Analytics",
  "challenge": "Difficulty engaging and leveraging large customer base",
  "solution": "Implementation of Clareefai Analytics for structured advocacy",
  "results": {
    "metrics": [
      {
        "name": "Active advocates",
        "value": "+120",
        "description": "Recruited 120 customer advocates"
      },
      {
        "name": "Pipeline impact",
        "value": "+€2M",
        "description": "Generated €2M in influenced pipeline"
      },
      {
        "name": "Sales cycle",
        "value": "-40%",
        "description": "Reduced average sales cycle time"
      }
    ],
    "roi": "350%",
    "timeframe": "12 months"
  },
  "benefits": [
    "Generated €2M influenced pipeline",
    "Reduced sales cycle by 40%",
    "Recruited 120 active advocates",
    "Improved customer retention by 25%"
  ],
  "status": "published",
  "featured": true,
  "publishDate": "2024-01-22T10:00:00Z",
  "requestDate": "2023-12-10T00:00:00Z",
  "submissionDate": "2024-01-15T14:00:00Z",
  "createdAt": "2023-12-10T00:00:00Z",
  "updatedAt": "2024-02-05T14:00:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'promoterId', type: 'string', required: true, description: 'ID du promoteur' },
        { name: 'type', type: 'string', required: true, description: 'Type: request ou submission' },
        { name: 'title', type: 'string', required: true, description: 'Titre du use case' },
        { name: 'slug', type: 'string', required: true, description: 'Slug URL-friendly' },
        { name: 'industry', type: 'string', required: true, description: 'Secteur d\'activité' },
        { name: 'challenge', type: 'string', required: true, description: 'Problématique client' },
        { name: 'solution', type: 'string', required: true, description: 'Solution apportée' },
        { name: 'results', type: 'object', required: true, description: 'Résultats obtenus' }
      ]
    },
    reviews: {
      title: 'reviews',
      description: 'Avis et évaluations clients',
      examples: [
        {
          name: 'Review Pending (en attente)',
          document: `{
  "_id": "review_uuid_001",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_003",
  "author": "Thomas Martin",
  "company": "InnovateTech",
  "rating": 4,
  "title": "Solid solution for growing companies",
  "comment": "Clareefai has helped us structure our customer advocacy efforts. Good product with room for improvement in reporting.",
  "product": "Clareefai Analytics",
  "sector": "Technology",
  "date": "2024-02-12T00:00:00Z",
  "status": "pending",
  "featured": false,
  "verified": false,
  "helpful": 0,
  "companyResponse": null,
  "platforms": {},
  "createdAt": "2024-02-12T00:00:00Z",
  "updatedAt": "2024-02-12T00:00:00Z"
}`
        },
        {
          name: 'Review Approved (approuvé)',
          document: `{
  "_id": "review_uuid_002",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_001",
  "author": "Michel Dubois",
  "company": "Tech Solutions",
  "rating": 5,
  "title": "Excellent solution for our needs",
  "comment": "Clareefai has exceeded our expectations in managing our customer advocacy program. The platform is intuitive and the support team is responsive.",
  "product": "Clareefai Analytics",
  "sector": "Technology",
  "date": "2024-01-15T00:00:00Z",
  "status": "approved",
  "featured": true,
  "verified": true,
  "helpful": 24,
  "companyResponse": {
    "text": "Thank you Michel! We're thrilled to hear Clareefai is helping your team succeed.",
    "respondedBy": "team_member_uuid_001",
    "respondedAt": "2024-01-16T09:30:00Z"
  },
  "platforms": {
    "g2": {
      "published": true,
      "url": "https://g2.com/reviews/clareefai-1",
      "publishedAt": "2024-01-17T00:00:00Z"
    }
  },
  "createdAt": "2024-01-15T00:00:00Z",
  "updatedAt": "2024-01-17T00:00:00Z"
}`
        },
        {
          name: 'Review Featured (mis en avant)',
          document: `{
  "_id": "review_uuid_003",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_002",
  "author": "Sophie Laurent",
  "company": "BigCorp International",
  "rating": 5,
  "title": "Game changer for enterprise customer advocacy",
  "comment": "Clareefai Analytics transformed how we engage with our customers. The ROI has been phenomenal - we've seen a 350% return in just 12 months. The platform is powerful yet easy to use, and the insights have been invaluable for our strategy.",
  "product": "Clareefai Analytics",
  "sector": "Finance",
  "date": "2024-01-20T00:00:00Z",
  "status": "approved",
  "featured": true,
  "verified": true,
  "helpful": 47,
  "companyResponse": {
    "text": "Sophie, thank you for sharing your experience! It's wonderful to see the impact Clareefai has had on BigCorp's advocacy program.",
    "respondedBy": "team_member_uuid_001",
    "respondedAt": "2024-01-21T10:15:00Z"
  },
  "platforms": {
    "g2": {
      "published": true,
      "url": "https://g2.com/reviews/clareefai-featured",
      "publishedAt": "2024-01-22T00:00:00Z"
    },
    "capterra": {
      "published": true,
      "url": "https://capterra.com/reviews/clareefai-featured",
      "publishedAt": "2024-01-23T00:00:00Z"
    }
  },
  "createdAt": "2024-01-20T00:00:00Z",
  "updatedAt": "2024-01-23T00:00:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'promoterId', type: 'string', required: true, description: 'ID du promoteur' },
        { name: 'rating', type: 'number', required: true, description: 'Note de 1 à 5' },
        { name: 'title', type: 'string', required: true, description: 'Titre de l\'avis' },
        { name: 'comment', type: 'string', required: true, description: 'Commentaire détaillé' },
        { name: 'status', type: 'string', required: true, description: 'Statut: pending, approved' },
        { name: 'verified', type: 'boolean', required: true, description: 'Avis vérifié' },
        { name: 'companyResponse', type: 'object', required: false, description: 'Réponse de l\'entreprise' }
      ]
    },
    rewards: {
      title: 'rewards',
      description: 'Récompenses et paiements aux promoteurs',
      examples: [
        {
          name: 'Reward Pending (en attente)',
          document: `{
  "_id": "reward_uuid_001",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_001",
  "promoterName": "Michel Dubois",
  "type": "global",
  "rewardType": "Review submission",
  "amount": 100,
  "currency": "EUR",
  "status": "pending",
  "date": "2024-02-15T00:00:00Z",
  "prospectId": null,
  "prospectName": null,
  "reason": "Reward for submitting a review on G2",
  "paymentMethod": null,
  "paymentDetails": null,
  "emailSent": false,
  "createdAt": "2024-02-15T00:00:00Z",
  "updatedAt": "2024-02-15T00:00:00Z"
}`
        },
        {
          name: 'Reward Sent',
          document: `{
  "_id": "reward_uuid_002",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_003",
  "promoterName": "Thomas Martin",
  "type": "specific",
  "rewardType": "Prospect conversion",
  "amount": 500,
  "currency": "EUR",
  "status": "sent",
  "date": "2024-02-10T00:00:00Z",
  "prospectId": "prospect_uuid_002",
  "prospectName": "Jean Dupont",
  "conversionValue": 75000,
  "reason": "Successful prospect conversion - Jean Dupont from Retail Solutions",
  "paymentMethod": "gift_card",
  "paymentDetails": {
    "provider": "Amazon",
    "giftCardCode": "XXXX-XXXX-XXXX-ABCD",
    "sentAt": "2024-02-10T16:00:00Z"
  },
  "emailSent": true,
  "createdAt": "2024-02-10T00:00:00Z",
  "updatedAt": "2024-02-10T16:00:00Z"
}`
        },
        {
          name: 'Reward Completed (payé)',
          document: `{
  "_id": "reward_uuid_003",
  "vendorId": "vendor_uuid_123",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "type": "specific",
  "rewardType": "Prospect conversion",
  "amount": 1500,
  "currency": "EUR",
  "status": "completed",
  "date": "2024-01-30T00:00:00Z",
  "prospectId": "prospect_uuid_003",
  "prospectName": "Marie Lambert",
  "conversionValue": 60000,
  "reason": "Successful high-value prospect conversion",
  "paymentMethod": "bank_transfer",
  "paymentDetails": {
    "reference": "PAY-2024-0015",
    "transactionId": "txn_abc123def456xyz",
    "paidAt": "2024-02-05T14:30:00Z",
    "bankName": "BNP Paribas",
    "accountLast4": "4567"
  },
  "emailSent": true,
  "createdAt": "2024-01-30T00:00:00Z",
  "updatedAt": "2024-02-05T14:30:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'promoterId', type: 'string', required: true, description: 'ID du promoteur' },
        { name: 'type', type: 'string', required: true, description: 'Type: global, specific, payment' },
        { name: 'rewardType', type: 'string', required: true, description: 'Type de récompense' },
        { name: 'amount', type: 'number', required: true, description: 'Montant' },
        { name: 'currency', type: 'string', required: true, description: 'Devise (EUR, USD...)' },
        { name: 'status', type: 'string', required: true, description: 'Statut: completed, pending, sent, paid' },
        { name: 'paymentDetails', type: 'object', required: false, description: 'Détails du paiement' }
      ]
    },
    teamMembers: {
      title: 'teamMembers',
      description: 'Membres de l\'équipe commerciale',
      examples: [
        {
          name: 'Sales Rep Active',
          document: `{
  "_id": "team_member_uuid_001",
  "vendorId": "vendor_uuid_123",
  "name": "Commercial A",
  "email": "commercial.a@company.com",
  "phone": "+33 1 23 45 67 89",
  "role": "sales",
  "status": "active",
  "permissions": ["view_prospects", "edit_prospects", "schedule_meetings"],
  "assignedProspects": 12,
  "conversions": 8,
  "conversionRate": 67,
  "totalRevenue": 450000,
  "performance": {
    "currentMonth": {
      "calls": 45,
      "meetings": 12,
      "conversions": 3,
      "revenue": 75000
    },
    "lastMonth": {
      "calls": 52,
      "meetings": 15,
      "conversions": 5,
      "revenue": 125000
    }
  },
  "availability": {
    "timezone": "Europe/Paris",
    "workingHours": {
      "monday": { "start": "09:00", "end": "18:00" },
      "tuesday": { "start": "09:00", "end": "18:00" },
      "wednesday": { "start": "09:00", "end": "18:00" },
      "thursday": { "start": "09:00", "end": "18:00" },
      "friday": { "start": "09:00", "end": "17:00" }
    }
  },
  "joinedDate": "2023-06-01T00:00:00Z",
  "createdAt": "2023-06-01T00:00:00Z",
  "updatedAt": "2024-02-15T08:30:00Z"
}`
        },
        {
          name: 'Admin User',
          document: `{
  "_id": "team_member_uuid_002",
  "vendorId": "vendor_uuid_123",
  "name": "Lisa Anderson",
  "email": "lisa@techcorp.com",
  "phone": "+33 1 98 76 54 32",
  "role": "admin",
  "status": "active",
  "permissions": ["all"],
  "assignedProspects": 0,
  "conversions": 0,
  "conversionRate": 0,
  "totalRevenue": 0,
  "performance": null,
  "availability": {
    "timezone": "Europe/Paris",
    "workingHours": {
      "monday": { "start": "08:30", "end": "17:30" },
      "tuesday": { "start": "08:30", "end": "17:30" },
      "wednesday": { "start": "08:30", "end": "17:30" },
      "thursday": { "start": "08:30", "end": "17:30" },
      "friday": { "start": "08:30", "end": "16:30" }
    }
  },
  "joinedDate": "2023-01-15T00:00:00Z",
  "createdAt": "2023-01-15T00:00:00Z",
  "updatedAt": "2024-02-15T09:00:00Z"
}`
        },
        {
          name: 'Sales Manager',
          document: `{
  "_id": "team_member_uuid_003",
  "vendorId": "vendor_uuid_123",
  "name": "Commercial B",
  "email": "commercial.b@company.com",
  "phone": "+33 6 12 34 56 78",
  "role": "sales",
  "status": "active",
  "permissions": ["view_prospects", "edit_prospects", "schedule_meetings", "manage_team"],
  "assignedProspects": 18,
  "conversions": 12,
  "conversionRate": 67,
  "totalRevenue": 720000,
  "performance": {
    "currentMonth": {
      "calls": 68,
      "meetings": 18,
      "conversions": 4,
      "revenue": 120000
    },
    "lastMonth": {
      "calls": 71,
      "meetings": 20,
      "conversions": 8,
      "revenue": 200000
    }
  },
  "availability": {
    "timezone": "Europe/Paris",
    "workingHours": {
      "monday": { "start": "08:00", "end": "18:00" },
      "tuesday": { "start": "08:00", "end": "18:00" },
      "wednesday": { "start": "08:00", "end": "18:00" },
      "thursday": { "start": "08:00", "end": "18:00" },
      "friday": { "start": "08:00", "end": "17:00" }
    }
  },
  "joinedDate": "2023-03-10T00:00:00Z",
  "createdAt": "2023-03-10T00:00:00Z",
  "updatedAt": "2024-02-15T08:45:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'name', type: 'string', required: true, description: 'Nom complet' },
        { name: 'email', type: 'string', required: true, description: 'Email' },
        { name: 'role', type: 'string', required: true, description: 'Rôle: admin, sales, support' },
        { name: 'status', type: 'string', required: true, description: 'Statut: active, inactive' },
        { name: 'permissions', type: 'array', required: true, description: 'Permissions accordées' },
        { name: 'performance', type: 'object', required: false, description: 'Métriques de performance' }
      ]
    },
    oneToOneCalls: {
      title: 'oneToOneCalls',
      description: 'Demandes d\'appels individuels entre promoteurs et prospects',
      examples: [
        {
          name: 'Call Request Pending',
          document: `{
  "_id": "call_uuid_001",
  "vendorId": "vendor_uuid_123",
  "type": "request",
  "promoterId": "promoter_uuid_001",
  "promoterName": "Michel Dubois",
  "requestorName": "Jennifer Martinez",
  "requestorEmail": "jennifer.martinez@startup.io",
  "requestorCompany": "Startup Innovations",
  "requestorPhone": "+1 555 234 5678",
  "status": "pending",
  "requestDate": "2024-02-14T00:00:00Z",
  "preferredDate": "2024-02-20",
  "preferredTime": "14:00",
  "timezone": "America/New_York",
  "topic": "Implementation best practices",
  "message": "I would like to discuss your experience with implementing Clareefai in a similar tech environment.",
  "scheduledDate": null,
  "meetingUrl": null,
  "completedDate": null,
  "createdAt": "2024-02-14T10:15:00Z",
  "updatedAt": "2024-02-14T10:15:00Z"
}`
        },
        {
          name: 'Call Scheduled',
          document: `{
  "_id": "call_uuid_002",
  "vendorId": "vendor_uuid_123",
  "type": "call",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "requestorName": "David Chen",
  "requestorEmail": "david.chen@fintech.com",
  "requestorCompany": "FinTech Solutions",
  "requestorPhone": "+44 20 1234 5678",
  "status": "scheduled",
  "requestDate": "2024-02-10T00:00:00Z",
  "preferredDate": "2024-02-18",
  "preferredTime": "15:00",
  "timezone": "Europe/London",
  "topic": "Enterprise deployment strategies",
  "message": "I'm interested in learning about your enterprise rollout experience with Clareefai.",
  "scheduledDate": "2024-02-18T15:00:00Z",
  "meetingUrl": "https://meet.google.com/abc-defg-hij",
  "completedDate": null,
  "notes": "Confirmed with both parties. Meeting invitation sent.",
  "createdAt": "2024-02-10T09:20:00Z",
  "updatedAt": "2024-02-12T14:30:00Z"
}`
        },
        {
          name: 'Call Completed',
          document: `{
  "_id": "call_uuid_003",
  "vendorId": "vendor_uuid_123",
  "type": "call",
  "promoterId": "promoter_uuid_002",
  "promoterName": "Sophie Laurent",
  "requestorName": "Emma Thompson",
  "requestorEmail": "emma.thompson@healthcare.com",
  "requestorCompany": "Healthcare Innovations",
  "requestorPhone": "+33 1 45 67 89 01",
  "status": "completed",
  "requestDate": "2024-01-20T00:00:00Z",
  "preferredDate": "2024-01-28",
  "preferredTime": "10:00",
  "timezone": "Europe/Paris",
  "topic": "ROI and business case development",
  "message": "Would love to understand the ROI you achieved with Clareefai to help build our business case.",
  "scheduledDate": "2024-01-28T10:00:00Z",
  "meetingUrl": "https://zoom.us/j/987654321",
  "completedDate": "2024-01-28T11:00:00Z",
  "notes": "Great call! Emma was very impressed with Sophie's insights. Potential high-value prospect.",
  "outcome": "positive",
  "followUp": "Emma requested introduction to sales team",
  "createdAt": "2024-01-20T14:25:00Z",
  "updatedAt": "2024-01-28T11:15:00Z"
}`
        }
      ],
      fields: [
        { name: '_id', type: 'string', required: true, description: 'Identifiant unique' },
        { name: 'vendorId', type: 'string', required: true, description: 'ID du vendor (isolation multi-tenant)' },
        { name: 'type', type: 'string', required: true, description: 'Type: request ou call' },
        { name: 'promoterId', type: 'string', required: true, description: 'ID du promoteur' },
        { name: 'requestorName', type: 'string', required: true, description: 'Nom du demandeur' },
        { name: 'requestorEmail', type: 'string', required: true, description: 'Email du demandeur' },
        { name: 'status', type: 'string', required: true, description: 'Statut: pending, scheduled, completed, cancelled' },
        { name: 'topic', type: 'string', required: false, description: 'Sujet de l\'appel' }
      ]
    }
  };

  const activeData = collectionData[activeCollection];

  return (
    <div className="flex h-[calc(100vh-200px)]">
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center space-x-2 mb-2">
            <Layers className="w-5 h-5 text-green-600" />
            <h2 className="font-bold text-gray-900">{t('settings.dataModel.collections')}</h2>
          </div>
          <p className="text-xs text-gray-600">{t('settings.dataModel.nosqlDb')}</p>
        </div>

        <div className="p-2">
          {collections.map((collection) => (
            <button
              key={collection.id}
              onClick={() => setActiveCollection(collection.id)}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg mb-1 transition-all ${
                activeCollection === collection.id
                  ? 'bg-green-100 border border-green-300'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <span className="text-xl">{collection.icon}</span>
              <span className={`text-sm font-medium ${
                activeCollection === collection.id ? 'text-green-900' : 'text-gray-700'
              }`}>
                {collection.name}
              </span>
            </button>
          ))}
        </div>

        <div className="p-4 mt-4 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-900 mb-1">{t('settings.dataModel.multiTenant')}</p>
                <p className="text-xs text-blue-700">
                  {t('settings.dataModel.multiTenantBody')} <code className="bg-blue-100 px-1 rounded">vendorId</code>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Database className="w-6 h-6 text-green-600" />
                    <h1 className="text-2xl font-bold text-gray-900">{activeData.title}</h1>
                  </div>
                  <p className="text-gray-600">{activeData.description}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Exemples de Documents</h3>

              <div className="space-y-4">
                {activeData.examples.map((example: any, index: number) => {
                  const exampleId = `${activeCollection}-example-${index}`;
                  const isExpanded = expandedExamples[exampleId] !== false;

                  return (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleExample(exampleId)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          )}
                          <span className="text-sm font-semibold text-gray-900">{example.name}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(example.document, exampleId);
                          }}
                          className="flex items-center space-x-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg border border-gray-300 transition-colors"
                        >
                          {copiedCode === exampleId ? (
                            <>
                              <Check className="w-3 h-3 text-green-600" />
                              <span className="text-green-600">Copié</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copier</span>
                            </>
                          )}
                        </button>
                      </button>

                      {isExpanded && (
                        <div className="p-4 bg-white">
                          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                            <code>{example.document}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="px-6 pb-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Description des Champs</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase bg-gray-50">Champ</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase bg-gray-50">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase bg-gray-50">Requis</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase bg-gray-50">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeData.fields.map((field: any, index: number) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <code className="text-sm font-mono text-green-600 bg-green-50 px-2 py-1 rounded">
                            {field.name}
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded font-mono">
                            {field.type}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            field.required
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {field.required ? 'Requis' : 'Optionnel'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">{field.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-amber-900 mb-3 flex items-center">
                <Database className="w-4 h-4 mr-2" />
                Indexation Recommandée
              </h3>
              <div className="space-y-2 text-xs text-amber-800">
                <div className="flex items-start space-x-2">
                  <span className="font-bold">•</span>
                  <code className="bg-amber-100 px-2 py-0.5 rounded flex-1">vendorId + email (unique)</code>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold">•</span>
                  <code className="bg-amber-100 px-2 py-0.5 rounded flex-1">vendorId + status</code>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold">•</span>
                  <code className="bg-amber-100 px-2 py-0.5 rounded flex-1">createdAt (desc)</code>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Sécurité Multi-Tenant
              </h3>
              <div className="space-y-2 text-xs text-blue-800">
                <div className="flex items-start space-x-2">
                  <span className="font-bold">•</span>
                  <span>Toujours filtrer par vendorId</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold">•</span>
                  <span>Validation au niveau application</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold">•</span>
                  <span>Utiliser les règles de sécurité DB</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Exemples de Requêtes MongoDB</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600 mb-1">Obtenir tous les documents du vendor:</p>
                <pre className="bg-gray-900 text-gray-100 rounded p-2 text-xs overflow-x-auto">
{`db.${activeData.title}.find({ vendorId: "vendor_uuid_123" })`}
                </pre>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Créer un index unique:</p>
                <pre className="bg-gray-900 text-gray-100 rounded p-2 text-xs overflow-x-auto">
{`db.${activeData.title}.createIndex({ vendorId: 1, email: 1 }, { unique: true })`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataModel;
